import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaGamepad } from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";

import { getOffers } from "../../services/googleSheetService";
import { filterOffersBySearch } from "../../utils/filterOffersBySearch";
import { fadeEdgeSlides } from "../../utils/fadeEdgeSlides";
import { getOfferCategory } from "../../utils/offerCategory";
import OfferSkeletons from "../../components/Loader/OfferSkeletons";
import OfferCard from "../../components/OfferCard/OfferCard";
import OffersSearch from "../../components/OffersSearch/OffersSearch";
import SwiperArrow from "../../components/SwiperArrow/SwiperArrow";
import StatusMessage from "../../components/StatusMessage/StatusMessage";
import styles from "./OffersPage.module.scss";

function CategoryOffersSwiper({ offers, categoryTitle }) {
  const swiperRef = useRef(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const updateNavState = (swiper) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
    fadeEdgeSlides(swiper);
  };

  return (
    <div className={`${styles.heroSwiper} ${!isEnd ? styles.fadeNext : ""}`}>
      {!isBeginning && (
        <SwiperArrow
          direction="prev"
          className="custom-prev"
          onClick={() => swiperRef.current?.slidePrev()}
        />
      )}

      <Swiper
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          updateNavState(swiper);
        }}
        onSlideChange={updateNavState}
        onResize={updateNavState}
        onBreakpoint={updateNavState}
        onUpdate={updateNavState}
        onProgress={fadeEdgeSlides}
        spaceBetween={18}
        slidesPerView={1.12}
        breakpoints={{
          650: { slidesPerView: 2.12 },
          900: { slidesPerView: 3.12 },
          1200: { slidesPerView: 3.8 },
        }}
      >
        {offers.map((offer) => (
          <SwiperSlide key={`${categoryTitle}-${offer.id}`}>
            <OfferCard offer={offer} variant="page" />
          </SwiperSlide>
        ))}
      </Swiper>

      {!isEnd && (
        <SwiperArrow
          direction="next"
          className="custom-next"
          onClick={() => swiperRef.current?.slideNext()}
        />
      )}
    </div>
  );
}

export default function OffersPage() {
  const [offers, setOffers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  const loadOffers = async () => {
    setLoading(true);
    setMessage("");

    try {
      const data = await getOffers();
      setOffers(data);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOffers();
  }, []);

  const filteredOffers = useMemo(
    () => filterOffersBySearch(offers, searchQuery),
    [offers, searchQuery]
  );

  const offersByCategory = useMemo(() => {
    const grouped = {};

    filteredOffers.forEach((offer) => {
      let category = getOfferCategory(offer);

      if (category.toLowerCase() === "home") {
        category = "Ofertas destacadas";
      }

      if (!grouped[category]) {
        grouped[category] = [];
      }

      grouped[category].push(offer);
    });

    return Object.entries(grouped)
      .map(([title, categoryOffers]) => ({
        title,
        offers: categoryOffers,
      }))
      .sort((a, b) => {
        if (a.title === "Ofertas destacadas") return 1;
        if (b.title === "Ofertas destacadas") return -1;
        return a.title.localeCompare(b.title, "es");
      });
  }, [filteredOffers]);

  const hasSearchQuery = searchQuery.trim().length > 0;

  return (
    <div className={styles.page}>
      {!message && (loading || offers.length > 0) && (
        <div className={styles.searchBar}>
          <OffersSearch
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </div>
      )}

      {loading && <OfferSkeletons sections={2} />}

      {!loading && message && (
        <StatusMessage variant="error">{message}</StatusMessage>
      )}

      {!loading && !message && offers.length === 0 && (
        <StatusMessage>No hay ofertas activas por el momento.</StatusMessage>
      )}

      {!loading &&
        !message &&
        hasSearchQuery &&
        offers.length > 0 &&
        offersByCategory.length === 0 && (
          <StatusMessage>
            No se encontraron ofertas con &quot;{searchQuery.trim()}&quot;.
          </StatusMessage>
        )}

      {!loading &&
        !message &&
        offersByCategory.map((category) => (
          <section className={styles.categoryBlock} key={category.title}>
            <div className={styles.categoryHeader}>
              <div className={styles.titleBox}>
                <span className={styles.icon}>
                  <FaGamepad />
                </span>
                <h3>{category.title}</h3>
              </div>

              <Link
                to={`/ofertas/${encodeURIComponent(category.title)}`}
                className={styles.viewAll}
              >
                Ver todas las ofertas <FaArrowRight />
              </Link>
            </div>

            <CategoryOffersSwiper
              offers={category.offers}
              categoryTitle={category.title}
            />
          </section>
        ))}
    </div>
  );
}

